import React, { useEffect } from 'react';
import { Steps } from 'antd';
import { CircleHelp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// @ts-ignore — SVG imported as React component via svg-react-loader
import BugHerdLogo from 'appJS/assets/images/bugherd-short-logo.svg';
import { getLangKey } from 'models/Language';
import * as translations from './strings';
import { WizardStep, STEP_PATHS } from './types';
import { trackVariantStepView } from './analytics';
import styles from './index.module.css';

type Props = {
  currentStep: WizardStep;
  firstName: string;
  activeExperiments: string[];
  userEmail?: string;
  children: React.ReactNode;
  rightPanel?: React.ReactNode;
};

const CONCIERGE_URL = 'https://bugherd.com/concierge';

const WizardShell: React.FC<Props> = ({
  currentStep,
  firstName,
  activeExperiments,
  userEmail,
  children,
  rightPanel
}) => {
  const strings = translations[getLangKey()];
  const navigate = useNavigate();

  // Each step is rendered by its own route, so this fires once per step view.
  useEffect(() => {
    trackVariantStepView(currentStep, activeExperiments, userEmail);
  }, [currentStep]);

  const handleStepClick = (index: number) => {
    if (index < currentStep) {
      navigate(STEP_PATHS[index as WizardStep]);
    }
  };

  const stepItems = [
    { title: strings.stepYourProject },
    { title: strings.stepInstall },
    { title: strings.stepTry }
  ].map((item, index) => ({
    ...item,
    className: index < currentStep ? styles.stepClickable : undefined
  }));

  return (
    <div className={styles.page}>
      <nav className={styles.topNav}>
        <div className={styles.navLeft}>
          <div className={styles.bhLogo}>
            <BugHerdLogo />
          </div>
          <p className={styles.navGreeting}>
            <span className={styles.navTextFull}>
              {strings.greeting(firstName)}
            </span>
            <span className={styles.navTextShort}>
              {strings.greetingShort(firstName)}
            </span>
          </p>
        </div>
        <div className={styles.navRight}>
          <span className={styles.navHelpText}>
            <span className={styles.navTextFull}>{strings.conciergeHelp}</span>
            <span className={styles.navTextShort}>
              {strings.conciergeHelpShort}
            </span>
          </span>
          <a
            href="https://bugherd.typeform.com/to/wIfihfHy"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.conciergeButton}
          >
            <CircleHelp size={16} />
            <span className={styles.conciergeLinkFull}>{strings.conciergeLink}</span>
            <span className={styles.conciergeLinkShort}>{strings.conciergeLinkShort}</span>
          </a>
        </div>
      </nav>

      <div className={styles.outerWrapper}>
        <div className={styles.onboardingVariantContainer}>
          <div className={styles.stepsBar}>
            <Steps
              size="small"
              current={currentStep}
              items={stepItems}
              onChange={handleStepClick}
            />
          </div>

          <div
            className={`${styles.onboardingVariantContent} ${
              rightPanel ? '' : styles.contentCentered
            }`}
          >
            <div className={styles.leftCol}>{children}</div>
            {rightPanel && <div className={styles.rightCol}>{rightPanel}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WizardShell;
